<template>
  <div class="building-detail-page">
    <div>
      <div class="mc-pixel-overlay"></div>
      <div class="mc-blocks-bg">
        <div class="mc-block grass"></div>
        <div class="mc-block stone"></div>
        <div class="mc-block dirt"></div>
        <div class="mc-block wood"></div>
        <div class="mc-block diamond"></div>
        <div class="mc-block gold"></div>
        <div class="mc-block iron"></div>
        <div class="mc-block coal"></div>
      </div>
      <div class="mc-items-float">
        <div class="mc-item sword">⚔️</div>
        <div class="mc-item pickaxe">⛏️</div>
        <div class="mc-item apple">🍎</div>
        <div class="mc-item chest">📦</div>
        <div class="mc-item torch">🔥</div>
        <div class="mc-item book">📚</div>
      </div>
      <div class="mc-scanlines"></div>
      <div class="mc-particles" aria-hidden="true">
        <span class="p p1"></span><span class="p p2"></span><span class="p p3"></span><span class="p p4"></span>
        <span class="p p5"></span><span class="p p6"></span><span class="p p7"></span><span class="p p8"></span>
        <span class="p p9"></span><span class="p p10"></span><span class="p p11"></span><span class="p p12"></span>
      </div>
    </div>

    <nav class="top-nav">
      <a href="javascript:void(0)" @click="goBack" class="back-link">&lt; ESCAPE TO TOWN</a>
      <div style="font-size: 10px;">WORLD_DATA // SNAPSHOT</div>
    </nav>

    <div class="content-scroll">
      <div class="title-group">
        <span class="corner-decoration corner-tl"></span>
        <span class="corner-decoration corner-tr"></span>
        <span class="corner-decoration corner-bl"></span>
        <span class="corner-decoration corner-br"></span>
        <h1 id="bTitle">{{ buildingData.title }}</h1>
        <div class="subtitle">PROJECT: {{ buildingData.subtitle }}</div>
      </div>

      <div class="carousel-wrapper-outer">
        <!-- 轮播两侧装饰区 -->
        <div class="carousel-decorations">
          <div class="mc-side-decor left">
            <div class="pixel-grass"></div>
            <div class="pixel-redstone"></div>
            <div class="pixel-particles">
              <span class="particle p1"></span><span class="particle p2"></span><span class="particle p3"></span>
            </div>
          </div>
          <div class="mc-side-decor right">
            <div class="pixel-stone"></div>
            <div class="pixel-diamond"></div>
            <div class="pixel-particles">
              <span class="particle p4"></span><span class="particle p5"></span><span class="particle p6"></span>
            </div>
          </div>
        </div>
        <div class="carousel-container">
        <div class="carousel-wrapper" id="carouselWrapper">
          <div
            v-for="(slide, index) in buildingData.slides"
            :key="index"
            class="slide"
            :class="{ active: index === currentSlide }"
          >
            <img :src="slide" @click="openLightbox(slide)" draggable="false" @dragstart.prevent />
          </div>
        </div>
        <button class="nav-btn prev" @click="moveSlide(-1)">&lt;</button>
        <button class="nav-btn next" @click="moveSlide(1)">&gt;</button>
      </div>

      <!-- 图片灯箱 -->
      <div v-if="lightboxOpen" class="lightbox-overlay" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <img 
            :src="lightboxImage" 
            :style="lightboxImageStyle"
            @wheel.prevent="onWheel"
            @mousedown="startDrag"
            @mousemove="onDrag"
            @mouseup="endDrag"
            @mouseleave="endDrag"
          />
          <button class="lightbox-close" @click="closeLightbox">&times;</button>
          <button class="lightbox-nav-btn lightbox-prev" @click.stop="navigateLightbox(-1)" :disabled="buildingData.slides.length <= 1">&lt;</button>
          <button class="lightbox-nav-btn lightbox-next" @click.stop="navigateLightbox(1)" :disabled="buildingData.slides.length <= 1">&gt;</button>
        </div>
      </div>
      </div>

      <section class="info-scroll">
        <div class="mc-header">
          <div class="mc-icon">🧪</div>
          <div class="mc-title">SNAPSHOT DATA</div>
        </div>

        <div class="meta-grid">
          <div class="meta-box mc-card">
            <div class="mc-card-header">
              <span class="mc-icon">📅</span>
              <span class="label">建成时间 / ARCHIVED DATE</span>
            </div>
            <div class="value" id="bDate">{{ buildingData.date }}</div>
          </div>
          <div class="meta-box mc-card">
            <div class="mc-card-header">
              <span class="mc-icon">👷</span>
              <span class="label">建筑师 / CHIEF ARCHITECT</span>
            </div>
            <div class="value" id="bAuthor">{{ buildingData.author }}</div>
          </div>
        </div>

        <div class="mc-divider"></div>

        <div class="mc-header">
          <div class="mc-icon">📖</div>
          <div class="mc-title">SNAPSHOT DESCRIPTION</div>
        </div>

        <div class="article-body mc-card" id="bDesc">
          <p v-for="(paragraph, index) in buildingData.description" :key="index">
            {{ paragraph }}
          </p>
        </div>

        <div class="mc-footer-info">
          <div class="mc-info-row">
            <span class="mc-info-key">ACCESS</span>
            <span class="mc-info-value">READ ONLY</span>
          </div>
          <div class="mc-info-row">
            <span class="mc-info-key">SOURCE</span>
            <span class="mc-info-value">USER NOTICE</span>
          </div>
        </div>
      </section>

      <footer class="footer-pixel">
        <div style="font-size: 10px; color: #444;">
          SNAPSHOT VIEW // THIS PROJECT MAY HAVE BEEN REMOVED FROM THE WORLD // 2026
        </div>
      </footer>
    </div>

    <div v-if="loadError" class="not-found">
      <div class="nf-title">快照不存在</div>
      <div class="nf-sub">SNAPSHOT: NOT FOUND</div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { API_BASE } from '../config/apiBase'

// API_BASE moved to @/config/apiBase


export default {
  name: 'BuildingSnapshot',
  setup() {
    const heroBg = ref(null)
    const route = useRoute()
    const router = useRouter()

    const currentSlide = ref(0)
    const loadError = ref(false)

    const lightboxOpen = ref(false)
    const lightboxImage = ref('')
    const lightboxScale = ref(1)
    const lightboxTranslateX = ref(0)
    const lightboxTranslateY = ref(0)
    const dragging = ref(false)
    const dragStartX = ref(0)
    const dragStartY = ref(0)
    const dragOriginX = ref(0)
    const dragOriginY = ref(0)

    const buildingData = ref({
      title: 'LOADING...',
      subtitle: 'PLEASE WAIT',
      date: '-',
      author: '-',
      slides: [],
      description: []
    })

    const heroBgStyle = computed(() => {
      if (buildingData.value.slides && buildingData.value.slides.length > 0 && buildingData.value.title !== 'LOADING...') {
        const currentSlideImg = buildingData.value.slides[currentSlide.value] || buildingData.value.slides[0]
        return `linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(10,10,10,1)), url(${currentSlideImg})`
      }
      return `radial-gradient(1200px 600px at 20% 10%, rgba(93, 231, 255, 0.12), transparent 55%),
    radial-gradient(900px 500px at 80% 20%, rgba(255, 59, 48, 0.10), transparent 60%),
    linear-gradient(to bottom, rgba(0, 0, 0, 0.15), rgba(10, 10, 10, 1))`
    })

    const parseJsonMaybe = (v) => {
      if (!v) return null
      if (typeof v === 'object') return v
      try {
        return JSON.parse(v)
      } catch (e) {
        return null
      }
    }

    const normalizeSnapshotToBuildingData = (snapshot) => {
      if (!snapshot) return null

      const title = snapshot.name || snapshot.title || 'UNKNOWN PROJECT'
      const subtitle = snapshot.subtitle || ''
      const date = snapshot.date || '-'
      const author = snapshot.author || '-'

      const cover = snapshot.coverImage || snapshot.cover_image || ''

      let slides = []
      const imagesRaw = snapshot.images
      const imagesArr = parseJsonMaybe(imagesRaw)
      if (Array.isArray(imagesArr)) {
        slides = imagesArr.filter(Boolean)
      }
      if (cover) {
        slides = [cover, ...slides.filter((x) => x !== cover)]
      }

      let description = []
      const descRaw = snapshot.description
      const descArr = parseJsonMaybe(descRaw)
      if (Array.isArray(descArr)) {
        description = descArr.map((x) => String(x))
      } else if (typeof descRaw === 'string' && descRaw.trim()) {
        description = [descRaw]
      }

      return { title, subtitle, date, author, slides, description }
    }

    const loadSnapshot = async () => {
      const id = route.params.id
      const token = localStorage.getItem('user_token')
      if (!token) {
        router.replace('/login')
        return
      }

      try {
        const res = await fetch(`${API_BASE}/user/notifications/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (!data || data.code !== 200 || !data.data) {
          loadError.value = true
          return
        }

        const notice = data.data
        const snapshot = parseJsonMaybe(notice.buildingSnapshot)
        const normalized = normalizeSnapshotToBuildingData(snapshot)
        if (!normalized) {
          loadError.value = true
          return
        }

        buildingData.value = normalized
      } catch (e) {
        loadError.value = true
      }
    }

    const moveSlide = (direction) => {
      const slides = buildingData.value.slides || []
      if (slides.length <= 1) return
      const next = (currentSlide.value + direction + slides.length) % slides.length
      currentSlide.value = next
    }

    const openLightbox = (imageSrc) => {
      const currentImage = buildingData.value.slides[currentSlide.value]
      lightboxImage.value = currentImage || imageSrc
      lightboxOpen.value = true
      lightboxScale.value = 1
      lightboxTranslateX.value = 0
      lightboxTranslateY.value = 0
      document.body.style.overflow = 'hidden'
    }

    const closeLightbox = () => {
      lightboxOpen.value = false
      document.body.style.overflow = ''
    }

    const navigateLightbox = (direction) => {
      const slides = buildingData.value.slides || []
      if (slides.length <= 1) return

      const next = (currentSlide.value + direction + slides.length) % slides.length
      currentSlide.value = next
      lightboxImage.value = slides[next]

      lightboxScale.value = 1
      lightboxTranslateX.value = 0
      lightboxTranslateY.value = 0
    }

    const onWheel = (e) => {
      const delta = e.deltaY > 0 ? 0.9 : 1.1
      const newScale = lightboxScale.value * delta
      if (newScale >= 0.5 && newScale <= 5) {
        lightboxScale.value = newScale
      }
    }

    const startDrag = (e) => {
      e.preventDefault()
      dragging.value = true
      dragStartX.value = e.clientX
      dragStartY.value = e.clientY
      dragOriginX.value = lightboxTranslateX.value
      dragOriginY.value = lightboxTranslateY.value
    }

    const onDrag = (e) => {
      if (!dragging.value) return
      e.preventDefault()
      const dx = e.clientX - dragStartX.value
      const dy = e.clientY - dragStartY.value
      lightboxTranslateX.value = dragOriginX.value + dx
      lightboxTranslateY.value = dragOriginY.value + dy
    }

    const endDrag = () => {
      dragging.value = false
    }

    const lightboxImageStyle = computed(() => ({
      transform: `translate(${lightboxTranslateX.value}px, ${lightboxTranslateY.value}px) scale(${lightboxScale.value})`,
      transition: dragging.value ? 'none' : 'transform 0.3s ease',
      cursor: dragging.value ? 'grabbing' : 'grab'
    }))

    const goBack = () => {
      router.back()
    }

    onMounted(async () => {
      await loadSnapshot()

      const handleKeydown = (e) => {
        if (!lightboxOpen.value) return

        switch (e.key) {
          case 'Escape':
            closeLightbox()
            break
          case 'ArrowLeft':
            navigateLightbox(-1)
            break
          case 'ArrowRight':
            navigateLightbox(1)
            break
        }
      }
      document.addEventListener('keydown', handleKeydown)
    })

    return {
      heroBg,
      heroBgStyle,
      buildingData,
      currentSlide,
      moveSlide,
      goBack,
      loadError,
      lightboxOpen,
      lightboxImage,
      lightboxImageStyle,
      openLightbox,
      closeLightbox,
      navigateLightbox,
      onWheel,
      startDrag,
      onDrag,
      endDrag
    }
  }
}
</script>

<style>
/* 建筑详情页专用样式 - 使用特定类名避免影响主页 */
.building-detail-page {
  margin: 0;
  padding: 0;
  background-color: #0a0a0a;
  font-family: 'Press Start 2P', 'Microsoft YaHei', cursive;
  color: #fff;
  overflow-x: hidden;
  min-height: 100vh;
}

.building-detail-page * {
  cursor: url('https://cur.cursors-4u.net/games/gam-4/gam385.cur'), auto !important;
  box-sizing: border-box;
}

.building-detail-page a,
.building-detail-page button,
.building-detail-page .nav-btn {
  cursor: url('https://cur.cursors-4u.net/games/gam-4/gam384.cur'), pointer !important;
}

:root {
  --mc-bg: #0a0a0a;
  --mc-oak: #c69c6d;
  --mc-oak-dark: #7d5a35;
  --mc-green: #5ea818;
  --mc-stone: #7a7a7a;
  --mc-paper: #e0d7c6;
  --mc-redstone: #ff3b30;
  --mc-redstone-dark: #b41610;
  --mc-diamond: #5de7ff;
  --mc-diamond-dark: #1aa7bf;
  --mc-emerald: #2cff7a;
  --mc-emerald-dark: #0e8f3c;
  --mc-obsidian: #1a1230;
}

/* --- 背景层：沉浸式动态背景 --- */
.building-detail-page .hero-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: radial-gradient(1200px 600px at 20% 10%, rgba(93, 231, 255, 0.12), transparent 55%),
    radial-gradient(900px 500px at 80% 20%, rgba(255, 59, 48, 0.10), transparent 60%),
    linear-gradient(to bottom, rgba(0, 0, 0, 0.15), rgba(10, 10, 10, 1));
    
  z-index: -1;
  transition: background 0.8s ease;
  overflow: hidden;
}

.building-detail-page .mc-pixel-overlay {
  position: absolute;
  inset: 0;
  background-image: 
    repeating-linear-gradient(0deg, rgba(0,0,0,0.15) 0px, transparent 1px, transparent 2px, rgba(0,0,0,0.15) 3px),
    repeating-linear-gradient(90deg, rgba(0,0,0,0.15) 0px, transparent 1px, transparent 2px, rgba(0,0,0,0.15) 3px);
  background-size: 8px 8px;
  opacity: 0.4;
  pointer-events: none;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.building-detail-page .mc-blocks-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}

.building-detail-page .mc-block {
  position: absolute;
  width: 32px;
  height: 32px;
  background-size: contain;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  opacity: 0.3;
  animation: mcBlockFloat 8s ease-in-out infinite;
}

.building-detail-page .mc-block.grass {
  top: 15%;
  left: 10%;
  background: linear-gradient(to bottom, #7cb342 0%, #7cb342 60%, #8d6e63 60%, #8d6e63 100%);
  animation-delay: 0s;
}

.building-detail-page .mc-block.stone {
  top: 25%;
  right: 15%;
  background: linear-gradient(135deg, #9e9e9e 0%, #757575 50%, #9e9e9e 100%);
  animation-delay: 1s;
}

.building-detail-page .mc-block.dirt {
  top: 60%;
  left: 8%;
  background: linear-gradient(135deg, #8d6e63 0%, #6d4c41 50%, #8d6e63 100%);
  animation-delay: 2s;
}

.building-detail-page .mc-block.wood {
  top: 70%;
  right: 12%;
  background: linear-gradient(90deg, #8d6e63 0%, #6d4c41 25%, #8d6e63 50%, #6d4c41 75%, #8d6e63 100%);
  animation-delay: 3s;
}

.building-detail-page .mc-block.diamond {
  top: 40%;
  left: 20%;
  background: linear-gradient(135deg, #5de7ff 0%, #1aa7bf 50%, #5de7ff 100%);
  animation-delay: 4s;
}

.building-detail-page .mc-block.gold {
  top: 55%;
  right: 25%;
  background: linear-gradient(135deg, #ffd700 0%, #ffb300 50%, #ffd700 100%);
  animation-delay: 5s;
}

.building-detail-page .mc-block.iron {
  top: 80%;
  left: 30%;
  background: linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 50%, #e0e0e0 100%);
  animation-delay: 6s;
}

.building-detail-page .mc-block.coal {
  top: 35%;
  right: 8%;
  background: linear-gradient(135deg, #424242 0%, #212121 50%, #424242 100%);
  animation-delay: 7s;
}

@keyframes mcBlockFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-20px) rotate(5deg);
    opacity: 0.5;
  }
}

.building-detail-page .mc-items-float {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.building-detail-page .mc-item {
  position: absolute;
  font-size: 24px;
  animation: mcItemFloat 6s ease-in-out infinite;
  filter: drop-shadow(2px 2px 4px rgba(0,0,0,0.5));
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.building-detail-page .mc-item.sword {
  top: 20%;
  left: 5%;
  animation-delay: 0s;
}

.building-detail-page .mc-item.pickaxe {
  top: 30%;
  right: 10%;
  animation-delay: 1s;
}

.building-detail-page .mc-item.apple {
  top: 50%;
  left: 15%;
  animation-delay: 2s;
}

.building-detail-page .mc-item.chest {
  top: 65%;
  right: 20%;
  animation-delay: 3s;
}

.building-detail-page .mc-item.torch {
  top: 75%;
  left: 25%;
  animation-delay: 4s;
}

.building-detail-page .mc-item.book {
  top: 45%;
  right: 30%;
  animation-delay: 5s;
}

@keyframes mcItemFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.6;
  }
  25% {
    transform: translateY(-15px) rotate(-5deg);
    opacity: 0.8;
  }
  75% {
    transform: translateY(-10px) rotate(5deg);
    opacity: 0.7;
  }
}

.building-detail-page .mc-scanlines {
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.0) 0px,
    rgba(0, 0, 0, 0.0) 6px,
    rgba(0, 0, 0, 0.12) 7px,
    rgba(0, 0, 0, 0.12) 8px
  );
  opacity: 0.35;
  animation: mcScan 4s linear infinite;
  pointer-events: none;
}

@keyframes mcScan {
  0% {
    transform: translateY(-20px);
  }
  100% {
    transform: translateY(20px);
  }
}

.building-detail-page .mc-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.building-detail-page .mc-particles .p {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.85);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.35);
  opacity: 0.0;
  animation: mcParticle 6.5s linear infinite;
}

.building-detail-page .mc-particles .p1 { left: 12%; top: 78%; animation-delay: 0s; }
.building-detail-page .mc-particles .p2 { left: 22%; top: 30%; animation-delay: 1s; background: var(--mc-diamond); }
.building-detail-page .mc-particles .p3 { left: 35%; top: 64%; animation-delay: 2s; }
.building-detail-page .mc-particles .p4 { left: 44%; top: 22%; animation-delay: 3s; background: var(--mc-emerald); }
.building-detail-page .mc-particles .p5 { left: 55%; top: 82%; animation-delay: 1.5s; }
.building-detail-page .mc-particles .p6 { left: 63%; top: 40%; animation-delay: 0.6s; background: var(--mc-redstone); }
.building-detail-page .mc-particles .p7 { left: 71%; top: 58%; animation-delay: 2.6s; }
.building-detail-page .mc-particles .p8 { left: 80%; top: 26%; animation-delay: 3.6s; }
.building-detail-page .mc-particles .p9 { left: 88%; top: 66%; animation-delay: 4.4s; background: var(--mc-diamond); }
.building-detail-page .mc-particles .p10 { left: 16%; top: 12%; animation-delay: 5s; }
.building-detail-page .mc-particles .p11 { left: 48%; top: 10%; animation-delay: 4.1s; }
.building-detail-page .mc-particles .p12 { left: 92%; top: 14%; animation-delay: 2.1s; background: var(--mc-redstone); }

@keyframes mcParticle {
  0% {
    transform: translateY(14px) scale(1);
    opacity: 0;
  }
  10% {
    opacity: 0.85;
  }
  60% {
    opacity: 0.35;
  }
  100% {
    transform: translateY(-220px) scale(0.6);
    opacity: 0;
  }
}

/* 顶部装饰条 */
.building-detail-page .top-nav {
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 100;
  padding: 30px 50px;
  display: flex;
  justify-content: space-between;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8), transparent);
}
.building-detail-page .back-link {
  text-decoration: none;
  color: #fff;
  font-size: 12px;
  padding: 10px 20px;
  border: 4px solid #fff;
  background: rgba(0, 0, 0, 0.5);
  transition: 0.3s;
}
.building-detail-page .back-link:hover {
  background: var(--mc-green);
  border-color: var(--mc-green);
}

/* --- 主内容布局：上下分布 --- */
.building-detail-page .content-scroll {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 96px;
}

/* 标题区 - Minecraft 现代UI风格 */
.building-detail-page .title-group {
  text-align: center;
  margin: 0 auto 50px auto;
  max-width: 900px;
  position: relative;
  padding: 40px 60px;
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 50%, #1e1e1e 100%);
  border: 6px solid #4a4a4a;
  border-radius: 8px;
  box-shadow: 
    0 0 0 4px #000,
    0 0 0 8px #4a4a4a,
    0 0 0 12px #000,
    0 8px 32px rgba(0, 0, 0, 0.8),
    inset 0 2px 4px rgba(255, 255, 255, 0.1),
    inset 0 -2px 4px rgba(0, 0, 0, 0.3);
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

/* 科技感纹理背景 */
.building-detail-page .title-group::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: 
    linear-gradient(45deg, transparent 48%, rgba(93, 231, 255, 0.03) 50%, transparent 52%),
    linear-gradient(-45deg, transparent 48%, rgba(255, 59, 48, 0.03) 50%, transparent 52%),
    repeating-linear-gradient(90deg, transparent 0px, transparent 19px, rgba(255, 255, 255, 0.02) 20px),
    repeating-linear-gradient(0deg, transparent 0px, transparent 19px, rgba(255, 255, 255, 0.02) 20px);
  border-radius: 4px;
  pointer-events: none;
}

/* 装饰角标 */
.building-detail-page .title-group::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
}
.building-detail-page .title-group .corner-decoration {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid #5de7ff;
  box-shadow: 0 0 10px rgba(93, 231, 255, 0.5);
}
.building-detail-page .title-group .corner-tl { 
  top: 10px; 
  left: 10px; 
  border-right: none;
  border-bottom: none;
}
.building-detail-page .title-group .corner-tr { 
  top: 10px; 
  right: 10px; 
  border-left: none;
  border-bottom: none;
}
.building-detail-page .title-group .corner-bl { 
  bottom: 10px; 
  left: 10px; 
  border-right: none;
  border-top: none;
}
.building-detail-page .title-group .corner-br { 
  bottom: 10px; 
  right: 10px; 
  border-left: none;
  border-top: none;
}

.building-detail-page .mc-block-decoration {
  position: relative;
}

.building-detail-page .mc-corner-tl,
.building-detail-page .mc-corner-tr,
.building-detail-page .mc-corner-bl,
.building-detail-page .mc-corner-br {
  position: absolute;
  width: 24px;
  height: 24px;
  border: 4px solid var(--mc-diamond);
  box-shadow: 0 0 10px var(--mc-diamond);
}

.building-detail-page .mc-corner-tl {
  top: 0;
  left: 20px;
  border-right: none;
  border-bottom: none;
}

.building-detail-page .mc-corner-tr {
  top: 0;
  right: 20px;
  border-left: none;
  border-bottom: none;
}

.building-detail-page .mc-corner-bl {
  bottom: 0;
  left: 20px;
  border-right: none;
  border-top: none;
}

.building-detail-page .mc-corner-br {
  bottom: 0;
  right: 20px;
  border-left: none;
  border-top: none;
}

.building-detail-page .title-group h1 {
  font-size: 48px;
  line-height: 1.1;
  color: #ffffff;
  text-shadow: 
    0 0 10px rgba(93, 231, 255, 0.8),
    0 0 20px rgba(93, 231, 255, 0.4),
    2px 2px 0 #000,
    4px 4px 0 rgba(0, 0, 0, 0.8);
  margin: 0 0 16px 0;
  font-family: 'Press Start 2P', 'Courier New', monospace;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  animation: titleGlow 3s ease-in-out infinite alternate;
}

@keyframes titleGlow {
  0% {
    filter: brightness(1) drop-shadow(0 0 5px rgba(93, 231, 255, 0.6));
  }
  100% {
    filter: brightness(1.2) drop-shadow(0 0 15px rgba(93, 231, 255, 0.9));
  }
}

@keyframes mcTitlePixel {
  0%, 100% {
    filter: brightness(1) contrast(1);
    transform: translateZ(0);
  }
  25% {
    filter: brightness(1.1) contrast(1.1);
    transform: translateZ(0) scale(1.02);
  }
  50% {
    filter: brightness(0.9) contrast(1.2);
    transform: translateZ(0) scale(1);
  }
  75% {
    filter: brightness(1.05) contrast(1.15);
    transform: translateZ(0) scale(1.01);
  }
}

.building-detail-page .subtitle {
  font-size: 14px;
  color: #5de7ff;
  letter-spacing: 6px;
  font-family: 'Courier New', monospace;
  text-shadow: 
    0 0 8px rgba(93, 231, 255, 0.8),
    2px 2px 0 #000,
    4px 4px 0 rgba(0, 0, 0, 0.6);
  text-transform: uppercase;
  font-weight: 600;
  animation: subtitlePulse 2s ease-in-out infinite alternate;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

@keyframes subtitlePulse {
  0% {
    opacity: 0.8;
    filter: brightness(1);
  }
  100% {
    opacity: 1;
    filter: brightness(1.3);
  }
}

@keyframes mcSubtitlePixel {
  0%, 100% {
    opacity: 1;
    transform: translateZ(0);
  }
  50% {
    opacity: 0.7;
    transform: translateZ(0) translateY(-2px);
  }
}

/* --- 1. 高级感轮播图 (3张以上) --- */
.building-detail-page .carousel-container {
  width: 100%;
  height: 100%;
  position: relative;
  border: 8px solid #4a4a4a !important;
  border-radius: 12px !important;
  box-shadow: 
    0 0 0 4px #000,
    0 0 0 8px #4a4a4a,
    0 0 0 12px #000,
    0 12px 40px rgba(0, 0, 0, 0.9),
    inset 0 2px 8px rgba(93, 231, 255, 0.2);
  flex-shrink: 0;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  background: #000;
  overflow: hidden;
  
}
.building-detail-page .carousel-wrapper {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  background: #000;
}
.building-detail-page .slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: 1s ease-in-out;
  transform: scale(1.1);
}
.building-detail-page .slide.active {
  opacity: 1;
  transform: scale(1);
}
.building-detail-page .slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 50% 50%;
  display: block;
  cursor: url('https://cur.cursors-4u.net/games/gam-4/gam384.cur'), pointer !important;
  transition: transform 0.3s ease;
}

.building-detail-page .slide img:hover {
  transform: scale(1.02);
}

/* 轮播外层包装器 */
.building-detail-page .carousel-wrapper-outer {
  position: relative;
  width: min(92vw, 1480px);
  aspect-ratio: 2560 / 1200;
  height: auto;
  margin: 0 auto 60px;
}

/* 轮播两侧装饰区 */
.building-detail-page .carousel-decorations {
  position: absolute;
  top: 0;
  left: -150px;
  right: -150px;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.building-detail-page .mc-side-decor {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 120px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-around;
}

.building-detail-page .mc-side-decor.left {
  left: 0;
}

.building-detail-page .mc-side-decor.right {
  right: 0;
}

/* 像素方块装饰 */
.building-detail-page .pixel-grass {
  width: 40px;
  height: 40px;
  background: linear-gradient(to bottom, #7cb342 0%, #7cb342 60%, #8d6e63 60%, #8d6e63 100%);
  border: 2px solid #5a8a2a;
  box-shadow: 0 0 10px rgba(124, 179, 66, 0.5);
  animation: pixelFloat 4s ease-in-out infinite;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.building-detail-page .pixel-redstone {
  width: 32px;
  height: 32px;
  background: radial-gradient(circle, #ff3b30 30%, #b41610 70%);
  border: 2px solid #8b0f08;
  box-shadow: 0 0 15px rgba(255, 59, 48, 0.8);
  animation: pixelPulse 2s ease-in-out infinite;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.building-detail-page .pixel-stone {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #9e9e9e 0%, #757575 50%, #9e9e9e 100%);
  border: 2px solid #616161;
  box-shadow: 0 0 8px rgba(158, 158, 158, 0.4);
  animation: pixelFloat 5s ease-in-out infinite;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

.building-detail-page .pixel-diamond {
  width: 28px;
  height: 28px;
  background: radial-gradient(circle, #5de7ff 20%, #1aa7bf 80%);
  border: 2px solid #158fa0;
  box-shadow: 0 0 20px rgba(93, 231, 255, 0.9);
  animation: pixelSpin 6s linear infinite;
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
}

/* 粒子效果 */
.building-detail-page .pixel-particles {
  position: relative;
  width: 60px;
  height: 60px;
}

.building-detail-page .particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(93, 231, 255, 0.8);
}

.building-detail-page .particle.p1 {
  top: 10px;
  left: 20px;
  animation: particleFloat 3s ease-in-out infinite;
}

.building-detail-page .particle.p2 {
  top: 30px;
  left: 10px;
  animation: particleFloat 3s ease-in-out infinite 0.5s;
}

.building-detail-page .particle.p3 {
  top: 20px;
  left: 40px;
  animation: particleFloat 3s ease-in-out infinite 1s;
}

.building-detail-page .particle.p4 {
  top: 15px;
  left: 25px;
  animation: particleFloat 3s ease-in-out infinite 0.3s;
  background: rgba(255, 59, 48, 0.8);
  box-shadow: 0 0 6px rgba(255, 59, 48, 0.8);
}

.building-detail-page .particle.p5 {
  top: 35px;
  left: 15px;
  animation: particleFloat 3s ease-in-out infinite 0.8s;
  background: rgba(255, 59, 48, 0.8);
  box-shadow: 0 0 6px rgba(255, 59, 48, 0.8);
}

.building-detail-page .particle.p6 {
  top: 25px;
  left: 45px;
  animation: particleFloat 3s ease-in-out infinite 1.3s;
  background: rgba(255, 59, 48, 0.8);
  box-shadow: 0 0 6px rgba(255, 59, 48, 0.8);
}

@keyframes pixelFloat {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(5deg);
  }
}

@keyframes pixelPulse {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
}

@keyframes pixelSpin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes particleFloat {
  0%, 100% {
    transform: translateY(0px);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-15px);
    opacity: 1;
  }
}

/* 轮播按钮 - 降低透明度，减少遮挡 */
.building-detail-page .nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.4), rgba(45, 45, 45, 0.4));
  border: 2px solid rgba(93, 231, 255, 0.6);
  color: rgba(255, 255, 255, 0.8);
  padding: 14px 18px;
  font-size: 16px;
  z-index: 10;
  cursor: url('https://cur.cursors-4u.net/games/gam-4/gam384.cur'), pointer !important;
  font-family: 'Press Start 2P', monospace;
  font-weight: bold;
  border-radius: 6px;
  box-shadow: 
    0 0 0 2px rgba(0, 0, 0, 0.3),
    0 0 8px rgba(93, 231, 255, 0.2),
    0 2px 6px rgba(0, 0, 0, 0.4),
    inset 0 1px 2px rgba(255, 255, 255, 0.1);
  image-rendering: pixelated;
  image-rendering: -moz-crisp-edges;
  image-rendering: crisp-edges;
  transition: all 0.3s ease;
  text-shadow: 0 0 4px rgba(93, 231, 255, 0.4);
  opacity: 0.6;
}

.building-detail-page .nav-btn:hover {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.8), rgba(45, 45, 45, 0.8));
  border-color: rgba(93, 231, 255, 0.9);
  color: #ffffff;
  transform: translateY(-50%) scale(1.05);
  box-shadow: 
    0 0 0 2px rgba(0, 0, 0, 0.5),
    0 0 15px rgba(93, 231, 255, 0.6),
    0 4px 12px rgba(0, 0, 0, 0.6),
    inset 0 1px 3px rgba(255, 255, 255, 0.2);
  text-shadow: 0 0 8px rgba(93, 231, 255, 0.8);
  opacity: 1;
}

.building-detail-page .nav-btn:active {
  transform: translateY(-50%) scale(0.98);
  box-shadow: 
    0 0 0 2px rgba(0, 0, 0, 0.5),
    0 0 12px rgba(93, 231, 255, 0.4),
    0 2px 8px rgba(0, 0, 0, 0.6),
    inset 0 1px 2px rgba(0, 0, 0, 0.2);
  opacity: 0.9;
}
.building-detail-page .nav-btn.prev {
  left: 20px;
}
.building-detail-page .nav-btn.next {
  right: 20px;
}

/* --- 2. 档案信息区 --- */
.building-detail-page .info-scroll {
  width: 100%;
  background: var(--mc-paper);
  color: #372511;
  padding: 100px 10%;
  border-top: 10px solid var(--mc-oak-dark);
  position: relative;
  box-shadow: 0 -20px 50px rgba(0, 0, 0, 0.5);
}
/* 卷轴边缘装饰 */
.building-detail-page .info-scroll::after {
  content: '';
  position: absolute;
  top: 20px;
  left: 20px;
  right: 20px;
  bottom: 20px;
  border: 2px dashed rgba(55, 37, 17, 0.2);
  pointer-events: none;
}

.building-detail-page .meta-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 50px;
  max-width: 1000px;
  margin: 0 auto 60px;
}
.building-detail-page .meta-box {
  border-bottom: 4px solid var(--mc-oak-dark);
  padding-bottom: 15px;
}
.building-detail-page .label {
  font-size: 16px;
  color: #7d5a35;
  display: block;
  margin-bottom: 10px;
}
.building-detail-page .value {
  font-size: 20px;
  font-weight: bold;
}

.building-detail-page .article-body {
  max-width: 1000px;
  margin: 0 auto;
  line-height: 2.2;
  font-size: 16px;
  text-align: justify;
}
.building-detail-page .article-body p {
  margin-bottom: 40px;
  text-indent: 2em;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* 底部装饰 */
.building-detail-page .footer-pixel {
  padding: 100px;
  text-align: center;
  background: #000;
  width: 100%;
}

.building-detail-page .mc-info-key {
  font-size: 12px;
  color: #7d5a35;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.building-detail-page .mc-info-value {
  font-size: 12px;
  color: #372511;
  font-family: 'Courier New', monospace;
}

/* 快照特有样式 */
.building-detail-page .not-found {
  position: fixed;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
  background: rgba(0,0,0,0.8);
}

.building-detail-page .nf-title {
  font-size: 26px;
  color: #fff;
  text-shadow: 4px 4px 0 #000;
}

.building-detail-page .nf-sub {
  margin-top: 12px;
  font-size: 12px;
  color: #9ae054;
}

/* 响应式优化 */
@media (max-width: 1200px) {
  .building-detail-page .title-group {
    max-width: 90%;
    padding: 32px 40px;
  }
  
  .building-detail-page .title-group h1 {
    font-size: 42px;
  }
  
  .building-detail-page .carousel-container {
    width: 100% !important;
    height: 100% !important;
  }

  .building-detail-page .carousel-decorations {
    left: -110px;
    right: -110px;
  }
}

@media (max-width: 768px) {
  .building-detail-page .content-scroll {
    padding-top: 80px;
    padding-left: 20px;
    padding-right: 20px;
  }
  
  .building-detail-page .title-group {
    max-width: 95%;
    padding: 24px 20px;
    margin-bottom: 30px;
  }
  
  .building-detail-page .title-group h1 {
    font-size: 32px;
    letter-spacing: 1px;
  }
  
  .building-detail-page .subtitle {
    font-size: 12px;
    letter-spacing: 4px;
  }
  
  .building-detail-page .carousel-container {
    width: 95% !important;
    height: 350px !important;
    margin-bottom: 40px !important;
  }
  
  .building-detail-page .nav-btn {
    padding: 12px 16px;
    font-size: 16px;
  }
  
  .building-detail-page .info-scroll {
    padding: 60px 5%;
  }
  
  .building-detail-page .meta-grid {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  .building-detail-page .mc-statbar {
    flex-direction: column;
    align-items: center;
  }
  
  .building-detail-page .mc-stat {
    width: 200px;
  }
  
  .building-detail-page .mc-actions {
    flex-direction: column;
    align-items: center;
  }
  
  .building-detail-page .mc-btn {
    width: 200px;
  }
}

@media (max-width: 480px) {
  .building-detail-page .top-nav {
    padding: 20px 20px;
    flex-direction: column;
    gap: 10px;
  }
  
  .building-detail-page .back-link {
    font-size: 10px;
    padding: 8px 16px;
  }
  
  .building-detail-page .title-group h1 {
    font-size: 24px;
  }
  
  .building-detail-page .subtitle {
    font-size: 10px;
    letter-spacing: 2px;
  }
  
  .building-detail-page .carousel-container {
    height: 100% !important;
  }

  .building-detail-page .carousel-decorations {
    left: -60px;
    right: -60px;
  }
  
  .building-detail-page .nav-btn {
    padding: 10px 12px;
    font-size: 14px;
  }
  
  .building-detail-page .info-scroll {
    padding: 40px 3%;
  }
}

/* 灯箱样式 */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.lightbox-content img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  box-shadow: 0 0 50px rgba(93, 231, 255, 0.5);
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: -40px;
  background: transparent;
  border: none;
  color: #fff;
  font-size: 40px;
  font-weight: bold;
  cursor: url('https://cur.cursors-4u.net/games/gam-4/gam384.cur'), pointer !important;
  transition: color 0.3s ease;
}

.lightbox-close:hover {
  color: #5de7ff;
}

.lightbox-nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.7), rgba(45, 45, 45, 0.7));
  border: 2px solid rgba(93, 231, 255, 0.6);
  color: rgba(255, 255, 255, 0.9);
  padding: 12px 16px;
  font-size: 18px;
  font-family: 'Press Start 2P', monospace;
  font-weight: bold;
  border-radius: 6px;
  cursor: url('https://cur.cursors-4u.net/games/gam-4/gam384.cur'), pointer !important;
  z-index: 1001;
  transition: all 0.3s ease;
  text-shadow: 0 0 4px rgba(93, 231, 255, 0.4);
  opacity: 0.7;
  pointer-events: auto;
}

.lightbox-nav-btn:hover {
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.9), rgba(45, 45, 45, 0.9));
  border-color: rgba(93, 231, 255, 0.9);
  color: #ffffff;
  opacity: 1;
  transform: translateY(-50%) scale(1.05);
  text-shadow: 0 0 8px rgba(93, 231, 255, 0.8);
}

.lightbox-nav-btn:active {
  transform: translateY(-50%) scale(0.98);
  opacity: 0.9;
}

.lightbox-nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.lightbox-nav-btn:disabled:hover {
  transform: translateY(-50%);
  background: linear-gradient(135deg, rgba(30, 30, 30, 0.7), rgba(45, 45, 45, 0.7));
  border-color: rgba(93, 231, 255, 0.6);
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 0 4px rgba(93, 231, 255, 0.4);
}

.lightbox-prev {
  left: 12px;
}

.lightbox-next {
  right: 12px;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>

